import { z } from 'zod';
// ── MTG primitives ────────────────────────────────────────────────────────────
export const FormatSchema = z.enum([
    'standard',
    'pioneer',
    'modern',
    'historic',
    'explorer',
    'alchemy',
    'brawl',
    'timeless',
    'limited',
    'pauper',
    'legacy',
    'vintage',
    'commander',
]);
export const MtgColorSchema = z.enum(['W', 'U', 'B', 'R', 'G', 'C']);
export const RaritySchema = z.enum(['common', 'uncommon', 'rare', 'mythic', 'special']);
export const CardLegalitySchema = z.enum(['legal', 'not_legal', 'restricted', 'banned']);
// ── Card ──────────────────────────────────────────────────────────────────────
export const CardImageUrisSchema = z
    .object({
    small: z.string().url().optional(),
    normal: z.string().url().optional(),
    large: z.string().url().optional(),
    png: z.string().url().optional(),
    art_crop: z.string().url().optional(),
    border_crop: z.string().url().optional(),
})
    .partial();
export const CardPriceSchema = z
    .object({
    usd: z.string().nullable().optional(),
    usd_foil: z.string().nullable().optional(),
    eur: z.string().nullable().optional(),
    tix: z.string().nullable().optional(),
})
    .partial();
export const CardSchema = z.object({
    id: z.string(),
    arenaId: z.number().int().nullable().optional(),
    name: z.string(),
    manaCost: z.string().nullable().optional(),
    cmc: z.number().nullable().optional(),
    colors: z.array(MtgColorSchema).nullable().optional(),
    typeLine: z.string().nullable().optional(),
    oracleText: z.string().nullable().optional(),
    rarity: RaritySchema.nullable().optional(),
    setCode: z.string().nullable().optional(),
    formatLegalities: z.record(FormatSchema, CardLegalitySchema).nullable().optional(),
    imageUris: CardImageUrisSchema.nullable().optional(),
    prices: CardPriceSchema.nullable().optional(),
    scryfallRaw: z.unknown().nullable().optional(),
});
// ── Deck ──────────────────────────────────────────────────────────────────────
export const DeckEntrySchema = z.object({
    cardName: z.string(),
    quantity: z.number().int().positive(),
    setCode: z.string().nullable().optional(),
    collectorNumber: z.string().nullable().optional(),
});
export const DeckSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(200),
    format: FormatSchema,
    ownerId: z.string(),
    mainboard: z.array(DeckEntrySchema),
    sideboard: z.array(DeckEntrySchema).nullable().optional(),
    source: z.string().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const DeckCreateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    format: FormatSchema,
    ownerId: z.string().default('rowan'),
    arenaPaste: z.string().optional(),
    mainboard: z.array(DeckEntrySchema).optional(),
    sideboard: z.array(DeckEntrySchema).optional(),
    source: z.string().optional(),
});
export const DeckUpdateSchema = DeckCreateSchema.partial();
// ── Match ─────────────────────────────────────────────────────────────────────
export const MatchResultSchema = z.enum(['win', 'loss', 'draw', 'concede']);
export const MatchSchema = z.object({
    id: z.string(),
    deckId: z.string().nullable().optional(),
    opponentArchetype: z.string().nullable().optional(),
    result: MatchResultSchema,
    format: FormatSchema,
    bestOf: z.number().int().min(1).max(3),
    playedAt: z.date(),
    rawLogEvent: z.unknown().nullable().optional(),
});
// ── Match ingest (Arena log → data-api wire payload) ─────────────────────────
export const WinningTeamSchema = z.union([z.literal(1), z.literal(2), z.literal('draw')]);
export const UserResultSchema = z.enum(['win', 'loss', 'draw']);
export const SeatSchema = z.union([z.literal(1), z.literal(2)]);
/** Which side of a game a fact belongs to, once seats are resolved. */
export const SideSchema = z.enum(['me', 'opp']);
export const GameCardIngestSchema = z.object({
    grpId: z.number().int(),
    cardName: z.string().nullable(),
    owner: SideSchema,
    firstSeenTurn: z.number().int().min(0),
    /** Null for a card only ever seen privately, i.e. our own hand. */
    firstPublicTurn: z.number().int().min(0).nullable(),
    /** A lower bound: the most copies visible at one instant. */
    maxCopiesSeen: z.number().int().min(0),
    zonesSeen: z.array(z.string()),
    cardTypes: z.array(z.string()),
    colors: z.array(z.string()),
    isLand: z.boolean(),
});
export const LifePointIngestSchema = z.object({
    turn: z.number().int().min(0),
    side: SideSchema,
    life: z.number().int(),
});
export const PlayEventIngestSchema = z.object({
    turn: z.number().int().min(0),
    side: SideSchema.nullable(),
    grpId: z.number().int().nullable(),
    cardName: z.string().nullable(),
});
export const DamageEventIngestSchema = z.object({
    turn: z.number().int().min(0),
    sourceGrpId: z.number().int().nullable(),
    sourceName: z.string().nullable(),
    /** `me`/`opp` for damage to a player, null when it hit a permanent. */
    targetSide: SideSchema.nullable(),
    amount: z.number().int(),
});
export const GameTimelineIngestSchema = z.object({
    landDrops: z.array(PlayEventIngestSchema),
    spellsCast: z.array(PlayEventIngestSchema),
    damage: z.array(DamageEventIngestSchema),
    /** Reconnect resyncs: `Full` snapshots after the first one in the game. */
    resyncs: z.number().int().min(0),
});
export const GameIngestSchema = z.object({
    gameNumber: z.number().int().positive(),
    /** Null when the game ended before a turn began. */
    onPlay: z.boolean().nullable(),
    myMulligans: z.number().int().min(0),
    oppMulligans: z.number().int().min(0),
    turns: z.number().int().min(0),
    result: UserResultSchema.nullable(),
    endReason: z.string().nullable(),
    startedAt: z.string().datetime().nullable(),
    endedAt: z.string().datetime().nullable(),
    finalLife: z.object({ me: z.number().int(), opp: z.number().int() }),
    lifeTrajectory: z.array(LifePointIngestSchema),
    cards: z.array(GameCardIngestSchema),
    timeline: GameTimelineIngestSchema,
    /** True when the stream began mid-game, so the board was never fully seeded. */
    partial: z.boolean(),
    gaps: z.number().int().min(0),
});
/**
 * Whether the reducer's per-game results agree with the match room's own
 * `finalMatchResult`. On `mismatch` the derived games are withheld rather than
 * published: a statistic the reducer cannot justify is worse than none.
 */
export const ReconciliationSchema = z.enum(['ok', 'mismatch', 'absent']);
/**
 * Payload posted by the Arena tail to `/api/matches/ingest`.
 * Cross-the-wire shape; produced by `reduceArenaEvent` in @ugin/shared.
 *
 * `format` is the Arena event name (e.g. `"Historic_Ladder"`), not the
 * normalized `Format` enum. We don't constrain it because Arena ships
 * event names we haven't catalogued (e.g. drafts, special events). The data-api
 * route maps Arena event → normalized format with a fallback.
 */
export const MatchIngestPayloadSchema = z.object({
    matchId: z.string().min(1),
    deckId: z.string().nullable(),
    deckName: z.string().nullable(),
    format: z.string().min(1),
    opponentName: z.string().min(1),
    userSeat: SeatSchema,
    userTeamId: SeatSchema,
    winningTeamId: WinningTeamSchema,
    userResult: UserResultSchema,
    reason: z.string(),
    startedAt: z.string().datetime(),
    completedAt: z.string().datetime(),
    // ── v2 additions. All defaulted, so a v1 payload still validates. ─────────
    opponentRank: z.string().nullable().default(null),
    bestOf: z.number().int().min(1).max(5).default(1),
    gameCount: z.number().int().min(0).default(1),
    games: z.array(GameIngestSchema).default([]),
    reconciliation: ReconciliationSchema.default('absent'),
});
export const RankSnapshotIngestSchema = z.object({
    ts: z.string().datetime(),
    constructedClass: z.string().nullable(),
    constructedLevel: z.number().int().min(0),
    constructedSeason: z.number().int().min(0),
    wins: z.number().int().min(0),
    losses: z.number().int().min(0),
    draws: z.number().int().min(0),
    limitedClass: z.string().nullable(),
    limitedLevel: z.number().int().min(0),
    limitedSeason: z.number().int().min(0),
});
export const DetailedLogsStateSchema = z.enum(['enabled', 'disabled', 'undetermined']);
/** Liveness beat from the tail, so the health surface can say what it last saw. */
export const ArenaHeartbeatSchema = z.object({
    logPath: z.string().nullable(),
    detailedLogs: DetailedLogsStateSchema,
    lastLogSeenAt: z.string().datetime().nullable(),
    lastMatchAt: z.string().datetime().nullable(),
    message: z.string().nullable().default(null),
});
// ── Meta ──────────────────────────────────────────────────────────────────────
export const ArchetypeSchema = z.object({
    name: z.string(),
    share: z.number().min(0).max(100),
    colors: z.array(MtgColorSchema).optional(),
    representativeDeck: z
        .object({
        mainboard: z.array(DeckEntrySchema),
        sideboard: z.array(DeckEntrySchema).optional(),
    })
        .optional(),
    sourceUrl: z.string().url().optional(),
});
export const MetaSnapshotSchema = z.object({
    id: z.string(),
    format: FormatSchema,
    source: z.string(),
    scrapedAt: z.date(),
    archetypes: z.array(ArchetypeSchema),
});
// ── Deck Analysis ─────────────────────────────────────────────────────────────
export const ManaCurveBucketSchema = z.object({
    cmc: z.number().int().min(0).max(7),
    label: z.string(),
    count: z.number().int().min(0),
});
export const ColorPieEntrySchema = z.object({
    color: MtgColorSchema,
    count: z.number().int().min(0),
    percentage: z.number().min(0).max(100),
});
export const TypeBreakdownSchema = z.object({
    creatures: z.number().int().min(0),
    spells: z.number().int().min(0),
    lands: z.number().int().min(0),
    artifacts: z.number().int().min(0),
    enchantments: z.number().int().min(0),
    planeswalkers: z.number().int().min(0),
    other: z.number().int().min(0),
});
export const ArchetypeLabelSchema = z.enum([
    'aggro',
    'midrange',
    'control',
    'combo',
    'tempo',
    'ramp',
    'tribal',
    'unknown',
]);
export const NearestMetaArchetypeSchema = z.object({
    archetype: z.string(),
    format: FormatSchema,
    overlapPercent: z.number().min(0).max(100),
    missingCards: z.array(DeckEntrySchema),
    extraCards: z.array(DeckEntrySchema),
});
export const DeckAnalysisSchema = z.object({
    id: z.string(),
    deckId: z.string(),
    computedAt: z.date(),
    manaCurve: z.array(ManaCurveBucketSchema),
    colorPie: z.array(ColorPieEntrySchema),
    typeBreakdown: TypeBreakdownSchema,
    archetypeLabel: ArchetypeLabelSchema.nullable().optional(),
    nearestMetaArchetype: NearestMetaArchetypeSchema.nullable().optional(),
});
// ── Usage tracking ────────────────────────────────────────────────────────────
export const ModelTierSchema = z.enum(['sonnet', 'opus', 'haiku']);
export const UsageRecordSchema = z.object({
    id: z.string(),
    ts: z.date(),
    source: z.string(),
    model: z.string(),
    tokensIn: z.number().int().min(0),
    tokensOut: z.number().int().min(0),
    cachedTokens: z.number().int().min(0).nullable().optional(),
    costUsd: z.number().min(0).nullable().optional(),
});
// ── Activity log ──────────────────────────────────────────────────────────────
export const ActivityStatusSchema = z.enum(['ok', 'warn', 'error']);
export const ActivityLogEntrySchema = z.object({
    id: z.string(),
    ts: z.date(),
    skill: z.string().nullable().optional(),
    action: z.string(),
    status: ActivityStatusSchema,
    message: z.string().nullable().optional(),
    details: z.unknown().nullable().optional(),
});
// ── Matchup matrix (for heatmap widget) ───────────────────────────────────────
export const MatchupCellSchema = z.object({
    deckId: z.string(),
    deckName: z.string(),
    /** Null when the opponent could not be classified. */
    archetype: z.string().nullable(),
    wins: z.number().int().min(0),
    losses: z.number().int().min(0),
    draws: z.number().int().min(0),
    winRate: z.number().min(0).max(1),
    /** Fewer than 20 matches: a number wearing a statistic's clothes. */
    provisional: z.boolean(),
});
export const MatchupMatrixSchema = z.object({
    format: FormatSchema,
    cells: z.array(MatchupCellSchema),
    generatedAt: z.date(),
});
// ── API envelope ──────────────────────────────────────────────────────────────
export const ApiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
export const HealthResponseSchema = z.object({
    status: z.literal('ok'),
    version: z.string(),
    uptime: z.number().min(0),
    dbOk: z.boolean(),
    tokenUsageMtd: z.number().min(0),
    lastScrape: z.string().nullable().optional(),
});
// ── Archetype characterization (meta-refresh → data-api) ─────────────────────
export const ArchetypeCardStatSchema = z.object({
    name: z.string().min(1),
    key: z.string().min(1),
    frequency: z.number().min(0).max(1),
    avgCopies: z.number().min(0),
    role: z.enum(['core', 'flex']),
});
export const ArchetypeCentroidIngestSchema = z.object({
    name: z.string().min(1),
    /** Percent of the published metagame, when the source reports one. */
    share: z.number().min(0).max(100).nullable().optional(),
    sampleSize: z.number().int().min(0),
    cards: z.array(ArchetypeCardStatSchema),
    deckUrls: z.array(z.string()).default([]),
});
export const ArchetypesIngestSchema = z.object({
    source: z.string().min(1),
    computedAt: z.string().datetime().optional(),
    archetypes: z.array(ArchetypeCentroidIngestSchema),
});
//# sourceMappingURL=index.js.map